import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { Feedback } from '../types';
import PageContainer from '../components/ui/PageContainer';

const Section: React.FC<{ kicker: string; title: string; children: React.ReactNode }> = ({
  kicker,
  title,
  children,
}) => (
  <Paper sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
        {kicker}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const Metric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Paper sx={{ p: 2, bgcolor: 'action.hover', height: '100%' }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
      {value}
    </Typography>
  </Paper>
);

const TextCard: React.FC<{ title: string; text?: string; highlight?: boolean }> = ({ title, text, highlight }) => (
  <Paper
    sx={{
      p: 2,
      height: '100%',
      bgcolor: highlight ? 'action.hover' : 'transparent',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
      {text}
    </Typography>
  </Paper>
);

const FeedbackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const viewer = useContext(AuthContext)?.user;
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadFeedback = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const response = await api.get<Feedback>(`/feedbacks/${id}`);
      setFeedback(response.data);
    } catch (err: any) {
      setError('Erro ao carregar feedback');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) loadFeedback();
  }, [id, loadFeedback]);

  const formatDate = useCallback(
    (value?: string): string => (value ? new Date(value).toLocaleDateString('pt-BR') : 'Não informado'),
    [],
  );

  const formatAdherence = useCallback((value?: string): string => {
    if (!value) return 'Não informado';
    if (value === 'sim') return 'Sim';
    if (value === 'parcialmente') return 'Parcialmente';
    return 'Não';
  }, []);

  const summaryCards = useMemo(
    () =>
      feedback
        ? [
            { label: 'Score final', value: feedback.finalScore, description: 'Resultado consolidado do ciclo.' },
            { label: 'Ciclo', value: feedback.cycle?.name || 'Não definido', description: 'Período vinculado à avaliação.' },
            { label: 'Data', value: formatDate(feedback.createdAt), description: 'Registro oficial do feedback.' },
          ]
        : [],
    [feedback, formatDate],
  );

  if (viewer?.role === 'developer') {
    return <Navigate to="/feedbacks" replace />;
  }

  if (loading) {
    return (
      <PageContainer>
        <Stack spacing={2} sx={{ py: 8, alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography color="text.secondary">Preparando feedback…</Typography>
        </Stack>
      </PageContainer>
    );
  }

  if (error || !feedback) {
    return (
      <PageContainer title="Feedback não encontrado">
        <Paper sx={{ p: 4, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ mb: 2 }}>{error || 'Feedback não encontrado'}</Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/feedbacks')}>
            Voltar para feedbacks
          </Button>
        </Paper>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      icon={<DescriptionOutlinedIcon />}
      title="Detalhes da avaliação"
      subtitle="Contexto do ciclo, critérios avaliados e consolidado com pontos fortes, melhorias e próximos passos."
      actions={
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/feedbacks')}>
          Voltar
        </Button>
      }
    >
      <Stack spacing={3}>
        <Grid container spacing={2}>
          {summaryCards.map((card) => (
            <Grid key={card.label} size={{ xs: 12, sm: 4 }}>
              <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
                  {card.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, my: 0.5 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Section kicker="Geral" title="Informações principais">
          <Grid container spacing={2}>
            {[
              { label: 'Desenvolvedor', value: feedback.developer?.name || 'Não informado' },
              { label: 'Gestor', value: feedback.manager?.name || 'Não informado' },
              { label: 'Ciclo', value: feedback.cycle?.name || 'Não definido' },
              { label: 'Data', value: formatDate(feedback.createdAt) },
            ].map((info) => (
              <Grid key={info.label} size={{ xs: 12, sm: 6, md: 3 }}>
                <Metric label={info.label} value={info.value} />
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section kicker="Produtividade" title="Produtividade técnica">
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Score', value: `${feedback.productivityScore}/5` },
              { label: 'Telas', value: feedback.screensDeveloped },
              { label: 'Tarefas', value: feedback.tasksCompleted },
              { label: 'Horas', value: feedback.hoursWorked },
              { label: 'Complexidade', value: `${feedback.averageComplexity}/10` },
            ].map((m) => (
              <Grid key={m.label} size={{ xs: 6, sm: 4, md: 2.4 }}>
                <Metric label={m.label} value={m.value} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: feedback.productivityObservations ? 6 : 12 }}>
              <TextCard title="Justificativa" text={feedback.productivityJustification} />
            </Grid>
            {feedback.productivityObservations && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextCard title="Observações" text={feedback.productivityObservations} />
              </Grid>
            )}
          </Grid>
        </Section>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Section kicker="Código" title="Qualidade do código">
              <Grid container spacing={2} sx={{ mb: feedback.codeQualityObservations ? 2 : 0 }}>
                <Grid size={6}>
                  <Metric label="Score" value={`${feedback.codeQualityScore}/5`} />
                </Grid>
                <Grid size={6}>
                  <Metric label="Aderência" value={formatAdherence(feedback.codeAdherence)} />
                </Grid>
              </Grid>
              {feedback.codeQualityObservations && (
                <TextCard title="Observações" text={feedback.codeQualityObservations} />
              )}
            </Section>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Section kicker="Testes" title="Validação e testes">
              <Grid container spacing={2} sx={{ mb: feedback.validationObservations ? 2 : 0 }}>
                <Grid size={6}>
                  <Metric label="Score" value={`${feedback.validationScore}/5`} />
                </Grid>
                <Grid size={6}>
                  <Metric label="Completude" value={formatAdherence(feedback.validationCompleteness)} />
                </Grid>
              </Grid>
              {feedback.validationObservations && (
                <TextCard title="Observações" text={feedback.validationObservations} />
              )}
            </Section>
          </Grid>
        </Grid>

        <Section kicker="Consolidado" title="Síntese do ciclo">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextCard title="Pontos fortes" text={feedback.strengths} highlight />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextCard title="Pontos de melhoria" text={feedback.improvements} highlight />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextCard title="Ações para o próximo ciclo" text={feedback.nextActions} highlight />
            </Grid>
          </Grid>
        </Section>
      </Stack>
    </PageContainer>
  );
};

export default FeedbackDetail;
